import { useState, useCallback } from 'react';
import { AuditLogEntry } from '../types';
import { getAuditLog, addAuditLogEntry } from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

export function useAuditLog(companyId: string | null) {
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    const data = await runAction((cid) => getAuditLog(cid));
    setAuditLog(data);
    return data;
  }, [runAction]);

  const addEntry = useCallback(async (entry: Omit<AuditLogEntry, 'id' | 'createdAt'>) => {
    const updated = await runAction((cid) => addAuditLogEntry(entry, cid));
    setAuditLog(updated);
    return updated;
  }, [runAction]);

  return { auditLog, setAuditLog, loading, error, load, addEntry };
}
